import React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
} from "@material-ui/core";
import EventAvailableIcon from "@material-ui/icons/EventAvailableRounded";
import AssignmentIcon from "@material-ui/icons/AssignmentRounded";
import WifiIcon from "@material-ui/icons/WifiRounded";
import PersonIcon from "@material-ui/icons/PersonRounded";
import FiberNewIcon from "@material-ui/icons/FiberNewRounded";
import ReplayIcon from "@material-ui/icons/ReplayRounded";
import EventBusyIcon from "@material-ui/icons/EventBusyRounded";
import LocalHospitalIcon from "@material-ui/icons/LocalHospitalRounded";
import { connect } from "react-redux";
import useStyles from "./style";
import { useEffect, useState } from "react";
import moment from "moment";
import { getDoctorDashboardRequest } from "./DoctorDashboardApiActions";
import {
  getDoctorAppointmentWithoutOrgIdRequest,
  editAppointmentRequest,
} from "../Appointments/AppointmentApiActions";
import GoogleCalendarCard from "./GoogleCalendarCard";

const DoctorHome = ({
  dashboardData,
  getDashboardData,
  user,
  appointments,
  getAllAppointments,
  updateAppointment,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const doctorId = user.id;
  // Ticks while a consultation is running so the elapsed time stays honest.
  const [now, setNow] = useState(() => Date.now());

  // Stamped server-side on the status transition, so these are the same times
  // every client sees.
  const checkInTimeOf = (appt) => appt.checkInTime || null;
  const checkOutTimeOf = (appt) => appt.checkOutTime || null;

  useEffect(() => {
    if (doctorId) {
      const todayStartTimestamp = moment().startOf("day").valueOf();
      getDashboardData(doctorId, todayStartTimestamp);
    }
  }, [doctorId, getDashboardData]);

  // The dashboard endpoint does not include appointmentType/followUpId per
  // appointment, so the online/offline breakdown is computed from the richer
  // `doctor/appointments` data (the same source the calendar uses).
  useEffect(() => {
    getAllAppointments();
  }, [getAllAppointments]);

  const handleViewAllClick = () => {
    history.push("/patients");
  };

  const getCaseId = (appt) => appt && (appt.CaseId || appt.caseId);
  const handleOpenCase = (appt) => {
    const caseId = getCaseId(appt);
    if (caseId) history.push(`/cases/${caseId}`);
  };

  const getPatientName = (appt) =>
    appt.Patient
      ? `${appt.Patient.firstName} ${appt.Patient.lastName || ""}`.trim()
      : appt.patientName || appt.title || "Unknown";

  const getPhone = (appt) =>
    (appt.Patient && (appt.Patient.mobile || appt.Patient.patientId)) || "";

  // Consultation lifecycle (reuses the existing `status` field):
  //   CONFIRMED -> CHECKED_IN (Start) -> COMPLETED (End)
  const STATUS = {
    CHECKED_IN: "CHECKED_IN",
    COMPLETED: "COMPLETED",
  };

  const rawStatus = (a) => (a.status || "").toUpperCase();
  const isCancelled = (a) => rawStatus(a).startsWith("CANCEL");
  // Single derivation of the queue status shown per appointment.
  const deriveStatus = (a) => {
    if (isCancelled(a))
      return { key: "CANCELLED", label: "Cancelled", color: "#757575", bg: "#f0f0f0" };
    if (rawStatus(a) === STATUS.COMPLETED)
      return { key: "COMPLETED", label: "Checked-out", color: "#1976D2", bg: "#e3f2fd" };
    if (rawStatus(a) === STATUS.CHECKED_IN)
      return { key: "IN_CONSULTATION", label: "In Consultation", color: "#00897B", bg: "#e0f2f1" };
    // Time passed and patient never checked in -> Missing.
    if (moment(a.endTime || a.startTime).isBefore(moment()))
      return { key: "MISSING", label: "Missing", color: "#d32f2f", bg: "#fdecea" };
    return { key: "WAITING", label: "Waiting", color: "#FB8C00", bg: "#fff4e5" };
  };

  // Today's appointments across the full consultation lifecycle so every status
  // can be shown (payment-pending/failed/cancelled are excluded). This single
  // set drives both the list and the today counts, so they always agree.
  const inLifecycle = (a) => {
    const s = rawStatus(a);
    return (
      s === "CONFIRMED" || s === STATUS.CHECKED_IN || s === STATUS.COMPLETED
    );
  };
  const todayAppointments = (appointments || [])
    .filter((a) => moment(a.startTime).isSame(moment(), "day") && inLifecycle(a))
    .sort((a, b) => a.startTime - b.startTime);

  const hasRunningConsultation = todayAppointments.some(
    (a) => rawStatus(a) === STATUS.CHECKED_IN && checkInTimeOf(a)
  );

  useEffect(() => {
    if (!hasRunningConsultation) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [hasRunningConsultation]);

  // Check-in / check-out reuse the existing appointment update endpoint, which
  // stamps checkInTime / checkOutTime from the status transition. The list is
  // refetched (without the full-screen loader) to pick up the new times.
  const setStatus = (appt, status) =>
    updateAppointment(appt.id, { status })
      .then(() => getAllAppointments(true))
      .catch(() => {});
  const handleStart = (appt) => setStatus(appt, STATUS.CHECKED_IN);
  const handleEnd = (appt) => setStatus(appt, STATUS.COMPLETED);

  const clockOf = (ms) => (ms ? moment(Number(ms)).format("hh:mm A") : "");

  const durationLabel = (ms) => {
    const mins = Math.max(0, Math.round(ms / 60000));
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
  };

  /**
   * The actual consultation times shown under the patient's name:
   *   In Consultation -> "In 10:32 AM · 12m elapsed" (ticks every 30s)
   *   Checked-out     -> "In 09:58 AM → Out 10:26 AM · 28m"
   * Nothing is rendered until the patient has been checked in.
   */
  const consultationTimes = (appt, statusKey) => {
    const inAt = checkInTimeOf(appt);
    const outAt = checkOutTimeOf(appt);
    // End pressed without Start: there is no start to report, only the close.
    if (!inAt) return outAt ? `Out ${clockOf(outAt)}` : "";
    if (outAt)
      return `In ${clockOf(inAt)} → Out ${clockOf(outAt)} · ${durationLabel(
        Number(outAt) - Number(inAt)
      )}`;
    if (statusKey === "IN_CONSULTATION")
      return `In ${clockOf(inAt)} · ${durationLabel(now - Number(inAt))} elapsed`;
    return `In ${clockOf(inAt)}`;
  };

  const greeting = (() => {
    const hour = moment().hour();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  })();

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  };

  // New vs Follow-up is only derivable if the appointment payload carries a
  // follow-up signal (e.g. followUpId); otherwise we show "-" instead of inventing values.
  const FOLLOWUP_KEYS = [
    "followUpId",
    "followupId",
    "follow_up_id",
    "isFollowUp",
    "followUp",
    "FollowUp",
  ];
  const hasFollowUpSignal = (appt) =>
    appt && FOLLOWUP_KEYS.some((key) => key in appt);
  const isFollowUp = (appt) =>
    !!(
      appt.followUpId ||
      appt.followupId ||
      appt.follow_up_id ||
      appt.isFollowUp ||
      appt.followUp ||
      appt.FollowUp
    );
  const canClassify = todayAppointments.some(hasFollowUpSignal);

  const byType = (type) =>
    todayAppointments.filter(
      (appt) => (appt.appointmentType || "").toLowerCase() === type
    );
  const offlineAppointments = byType("offline");
  const onlineAppointments = byType("online");

  const splitOf = (list) => {
    if (!canClassify) return { newCount: "-", followUpCount: "-" };
    const followUpCount = list.filter(isFollowUp).length;
    return { newCount: list.length - followUpCount, followUpCount };
  };
  const offlineSplit = splitOf(offlineAppointments);
  const onlineSplit = splitOf(onlineAppointments);

  const renderTypeCard = ({ title, icon, accentBg, total, split }) => (
    <Card className={classes.typeCard}>
      <CardContent>
        <Box className={classes.typeHeader}>
          <Box
            className={classes.typeIconWrap}
            style={{ backgroundColor: accentBg }}
          >
            {icon}
          </Box>
          <Typography className={classes.typeTitle}>{title}</Typography>
        </Box>

        <Typography className={classes.typeTotalLabel}>
          Total {title}
        </Typography>
        <Typography className={classes.typeTotalValue}>{total}</Typography>

        <Box className={classes.splitRow}>
          <Box className={`${classes.splitPanel} ${classes.newPanel}`}>
            <Box className={classes.splitPanelHeader}>
              <FiberNewIcon style={{ fontSize: "1.05rem", color: "#3b82f6" }} />
              <Typography className={classes.splitLabel}>New</Typography>
            </Box>
            <Typography className={classes.splitValue}>
              {split.newCount}
            </Typography>
          </Box>

          <Box className={`${classes.splitPanel} ${classes.followPanel}`}>
            <Box className={classes.splitPanelHeader}>
              <ReplayIcon style={{ fontSize: "1.05rem", color: "#8b5cf6" }} />
              <Typography className={classes.splitLabel}>Follow-up</Typography>
            </Box>
            <Typography className={classes.splitValue}>
              {split.followUpCount}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box className={classes.root}>
      {/* One-time Google sign-in so this doctor's online consultations get a
          Meet link with them as host. Renders nothing once connected. */}
      <GoogleCalendarCard />

      <Box className={classes.header}>
        <Typography className={classes.sectionTitle}>Overview</Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Card className={classes.welcomeCard} style={{ height: "100%" }}>
            <CardContent className={classes.welcomeContent}>
              <Avatar className={classes.welcomeAvatar}>
                {getInitials(dashboardData.doctorName) || "Dr"}
              </Avatar>
              <Typography className={classes.greetingText}>
                {greeting},
              </Typography>
              <Typography className={classes.welcomeName}>
                Dr. {dashboardData.doctorName || "Doctor"}
              </Typography>
              <Box className={classes.apptCountPill}>
                <EventAvailableIcon style={{ fontSize: "1.05rem" }} />
                {todayAppointments.length} appointments today
              </Box>
            </CardContent>

            <LocalHospitalIcon className={classes.welcomeBgIcon} />
            <Box className={classes.welcomeGlow} />
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card className={classes.statsCard} style={{ height: "120px" }}>
                <CardContent style={{ padding: "16px" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Typography variant="body1" color="textSecondary">
                      Today's Appointments
                    </Typography>
                    <Box
                      className={classes.typeIconWrap}
                      style={{ backgroundColor: "#e0e7ff", width: 36, height: 36 }}
                    >
                      <EventAvailableIcon
                        style={{ fontSize: "20px", color: "#6366f1" }}
                      />
                    </Box>
                  </Box>
                  <Typography className={classes.customTypography}>
                    {todayAppointments.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Card className={classes.statsCard} style={{ height: "120px" }}>
                <CardContent style={{ padding: "16px" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Typography variant="body1" color="textSecondary">
                      Total Appointments
                    </Typography>
                    <Box
                      className={classes.typeIconWrap}
                      style={{ backgroundColor: "#dbeafe", width: 36, height: 36 }}
                    >
                      <AssignmentIcon
                        style={{ fontSize: "20px", color: "#3b82f6" }}
                      />
                    </Box>
                  </Box>
                  <Typography className={classes.customTypography}>
                    {dashboardData.allTimeAppointments ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderTypeCard({
                title: "Offline Appointments",
                icon: <PersonIcon style={{ color: "#4f46e5" }} />,
                accentBg: "#eef2ff",
                total: offlineAppointments.length,
                split: offlineSplit,
              })}
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderTypeCard({
                title: "Online Appointments",
                icon: <WifiIcon style={{ color: "#0a8f54" }} />,
                accentBg: "#e7f7ee",
                total: onlineAppointments.length,
                split: onlineSplit,
              })}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={3} style={{ marginTop: 8 }}>
        <Grid item xs={12}>
          <Card className={classes.appointmentsCard} style={{ height: "auto" }}>
            <CardContent>
              <Box className={classes.appointmentHeader}>
                <Typography className={classes.cardTitle}>
                  Today's Appointments
                </Typography>
                <Button
                  className={classes.viewAllButton}
                  onClick={handleViewAllClick}
                >
                  View All
                </Button>
              </Box>

              {todayAppointments.length === 0 ? (
                <Box className={classes.emptyState}>
                  <EventBusyIcon className={classes.emptyIcon} />
                  <Typography variant="body1">
                    No appointments scheduled for today
                  </Typography>
                </Box>
              ) : (
                <List>
                  {todayAppointments.map((appointment) => {
                    const type = (
                      appointment.appointmentType || ""
                    ).toLowerCase();
                    const status = deriveStatus(appointment);
                    const times = consultationTimes(appointment, status.key);
                    const canOpenCase = !!getCaseId(appointment);
                    return (
                      <ListItem
                        key={appointment.id}
                        className={classes.apptListItem}
                      >
                        <ListItemAvatar
                          onClick={() => handleOpenCase(appointment)}
                          style={{
                            cursor: canOpenCase ? "pointer" : "default",
                          }}
                        >
                          <Avatar
                            className={classes.appointmentAvatar}
                            src={
                              appointment.Patient && appointment.Patient.imageUrl
                                ? appointment.Patient.imageUrl
                                : undefined
                            }
                          >
                            {appointment.initials ||
                              getInitials(getPatientName(appointment))}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          onClick={() => handleOpenCase(appointment)}
                          style={{
                            cursor: canOpenCase ? "pointer" : "default",
                          }}
                          primary={
                            <Typography
                              variant="body1"
                              style={{ fontWeight: 500 }}
                            >
                              {getPatientName(appointment)}
                              {getPhone(appointment)
                                ? ` - ${getPhone(appointment)}`
                                : ""}
                            </Typography>
                          }
                          secondary={
                            <span className={classes.apptSecondary}>
                              <span
                                className={classes.queueChip}
                                style={{
                                  color: status.color,
                                  backgroundColor: status.bg,
                                }}
                              >
                                {status.label}
                              </span>
                              {times ? (
                                <span className={classes.apptTimes}>
                                  {times}
                                </span>
                              ) : null}
                            </span>
                          }
                        />
                        {status.key === "WAITING" && (
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            className={classes.startBtn}
                            onClick={() => handleStart(appointment)}
                          >
                            Start
                          </Button>
                        )}
                        {status.key === "IN_CONSULTATION" && (
                          <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            className={classes.endBtn}
                            onClick={() => handleEnd(appointment)}
                          >
                            End
                          </Button>
                        )}
                        <Box className={classes.apptMeta}>
                          {type ? (
                            <Chip
                              size="small"
                              label={type}
                              className={
                                type === "online"
                                  ? classes.onlineBadge
                                  : classes.offlineBadge
                              }
                            />
                          ) : null}
                          <Typography className={classes.apptMetaTime}>
                            {moment(appointment.startTime).format("hh:mm A")}
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  dashboardData: state.doctorDashboard.dashboardData,
  user: state.auth.loggedInUser,
  appointments: state.appointments.appointments,
});

const mapDispatchToProps = (dispatch) => {
  return {
    getDashboardData: (doctorId, date) => {
      return dispatch(getDoctorDashboardRequest(doctorId, date)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAllAppointments: (isHideLoader = false) => {
      return dispatch(
        getDoctorAppointmentWithoutOrgIdRequest(isHideLoader)
      ).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    updateAppointment: (id, data) => {
      return dispatch(editAppointmentRequest(id, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DoctorHome);
